// Atlas DEM fetch (Part VI / M30, ELV-1). Client-side — network lives here, the
// resample is pure (`sim/atlas/elevation.ts`). Pulls an open elevation raster for
// the world bbox (SRTM 1-arc-sec ≈30 m, public domain, via OpenTopoData), one
// polite tiled request cached per tile. Any failure (timeout / HTTP / parse)
// yields null so the caller falls back to flat terrain (`flatTerrain:true`). The
// default KHFD location never calls this — it reads the bundled fixture
// (`e2e/fixtures/dem-khfd.json`), offline-first (ATL-6).
import type { DemTile, LatLon } from '../../sim/atlas/elevation';
import { NO_DATA } from '../../sim/atlas/elevation';
import { ELV } from '../../sim/config';
import { bboxAround } from './geocode';

const DEM_SOURCE = 'SRTM 1-arc-sec (~30 m) via OpenTopoData';
const ENDPOINT = 'https://api.opentopodata.org/v1/srtm30m';
const MAX_PER_REQUEST = 100; // OpenTopoData caps a batch at 100 locations.

/** Per-tile cache (ELV-1), keyed by a rounded bbox + resolution. */
const cache = new Map<string, DemTile | null>();

export interface DemFetchOpts {
  samples?: number;
  timeoutMs?: number;
  fetchImpl?: typeof globalThis.fetch;
  signal?: AbortSignal;
  /** Polite spacing between batched requests (ms); the tool uses this to stay under
   *  the public endpoint's rate limit when building the frozen fixture. */
  batchDelayMs?: number;
}

/** The row-major (NW-origin) lat/lon sample grid over a bbox — shared by the live
 *  fetch and the fixture generator so both rasters align exactly. */
export function demGridCoords(
  south: number, west: number, north: number, east: number, samples: number,
): LatLon[] {
  const out: LatLon[] = [];
  const n = Math.max(2, samples);
  for (let r = 0; r < n; r++) {
    const lat = north + (south - north) * (r / (n - 1)); // row 0 = north edge
    for (let c = 0; c < n; c++) {
      const lon = west + (east - west) * (c / (n - 1));
      out.push({ lat, lon });
    }
  }
  return out;
}

const tileKey = (s: number, w: number, n: number, e: number, samples: number): string =>
  [s, w, n, e].map((v) => v.toFixed(5)).join(',') + `@${samples}`;

/** Fetch an elevation tile for the bbox around `center` (ELV-1). Batches the grid
 *  into ≤100-coord requests under one 10 s deadline; any error → null. */
export async function fetchDemTile(
  center: LatLon, bboxM: number, opts: DemFetchOpts = {},
): Promise<DemTile | null> {
  const samples = opts.samples ?? ELV.TILE_SAMPLES;
  const [south, west, north, east] = bboxAround(center, bboxM);
  const key = tileKey(south, west, north, east, samples);
  if (cache.has(key)) return cache.get(key)!;

  const doFetch = opts.fetchImpl ?? globalThis.fetch;
  const ctrl = new AbortController();
  const timer = setTimeout(() => ctrl.abort(), opts.timeoutMs ?? ELV.FETCH_TIMEOUT_MS);
  opts.signal?.addEventListener('abort', () => ctrl.abort());

  try {
    const coords = demGridCoords(south, west, north, east, samples);
    const elev = new Array<number>(coords.length);
    const delay = opts.batchDelayMs ?? 0;
    for (let off = 0; off < coords.length; off += MAX_PER_REQUEST) {
      if (off > 0 && delay > 0) await new Promise((r) => setTimeout(r, delay));
      const batch = coords.slice(off, off + MAX_PER_REQUEST);
      const locations = batch.map((p) => `${p.lat.toFixed(6)},${p.lon.toFixed(6)}`).join('|');
      const res = await doFetch(`${ENDPOINT}?locations=${encodeURIComponent(locations)}`, {
        headers: { 'Accept': 'application/json', 'User-Agent': 'TerraForge/1.0 (education; contact via github)' },
        signal: ctrl.signal,
      });
      if (!res.ok) throw new Error(`dem HTTP ${res.status}`);
      const json = (await res.json()) as { status?: string; results?: Array<{ elevation: number | null }> };
      const results = json.results;
      if (json.status !== 'OK' || !Array.isArray(results) || results.length !== batch.length) throw new Error('dem: malformed response');
      for (let i = 0; i < results.length; i++) {
        const v = results[i]!.elevation;
        elev[off + i] = v == null || !Number.isFinite(v) ? NO_DATA : v;
      }
    }
    const tile: DemTile = { width: samples, height: samples, south, west, north, east, elev, source: DEM_SOURCE };
    cache.set(key, tile);
    return tile;
  } catch (err) {
    console.warn('[atlas] DEM fetch failed → flat terrain (ELV-1):', err);
    cache.set(key, null);
    return null;
  } finally {
    clearTimeout(timer);
  }
}

/** Test/tool hook: clear the per-tile cache. */
export function clearDemCache(): void { cache.clear(); }

export { DEM_SOURCE };
